<template>
    <div class="DashboardsDrawerPanel">
        <div v-if="!editMode">
            <q-btn
                color="primary"
                dense
                icon="edit"
                noCaps
                outline
                class="full-width"
                :label="$t('dashboards.dashboardButtons.edit')"
                @click="$emit('on-enter-edit')"
            />
        </div>

        <div v-else>
            <div class="row justify-between items-center q-mt-sm q-pb-md no-wrap">
                <q-btn
                    icon="save"
                    :label="$t('dashboards.dashboardButtons.save')"
                    class="q-ma-xs"
                    noCaps
                    dense
                    color="primary"
                    outline
                    @click="$emit('on-save-edit')"
                />
                <q-btn
                    icon="close"
                    :label="$t('dashboards.dashboardButtons.close')"
                    noCaps
                    dense
                    flat
                    color="primary"
                    class="q-ma-xs"
                    @click="$emit('on-cancel-edit')"
                />
            </div>

            <div class="row no-wrap items-center">
                <div class="text-bold">
                    {{ graphObj ? graphName : 'Dashboard' }}
                </div>
                <q-btn
                    v-if="graphObj"
                    icon="edit"
                    unelevated
                    flat
                    dense
                    noCaps
                    color="white"
                    textColor="primary"
                    @click="openEditName = true"
                />
            </div>

            <ModalNameDesc
                v-if="graphObj"
                ref="openEditName"
                v-model="openEditName"
                :title="$t('dashboards.editNameModal.title')"
                :action="$t('dashboards.editNameModal.rename')"
                withoutDesc
                @on-submit="handleRename"
            />

            <q-tabs
                v-model="localTab"
                noCaps
                activeColor="primary"
                indicatorColor="primary"
                class="tabs-label text-grey-8 q-mt-sm"
                @update:modelValue="$emit('on-set-side-tab', $event)"
            >
                <q-tab
                    :name="graphObj ? 'graph' : 'graphsList'"
                    :label="graphObj ? $t('dashboards.dashboardButtons.configure') : $t('dashboards.dashboardButtons.addGraph')"
                />
                <q-tab
                    name="filters"
                    :label="$t('dashboards.dashboardButtons.filters')"
                />
            </q-tabs>

            <q-tab-panels v-model="localTab">
                <q-tab-panel name="graphsList" class="q-pa-none">
                    <q-expansion-item
                        v-for="ei in graficas"
                        :key="ei.key"
                        group="graficas"
                        dense
                        denseToggle
                        headerClass="q-py-sm text-primary text-weight-bold"
                        :label="ei.label"
                    >
                        <q-list separator>
                            <q-item
                                v-for="li in ei.content"
                                :key="li.key"
                                clickable
                                class="q-px-lg"
                            >
                                <q-item-section @click="$emit('on-add-graph', { type: li.key, group: ei.key })">
                                    {{ li.label }}
                                </q-item-section>
                            </q-item>
                        </q-list>
                    </q-expansion-item>
                </q-tab-panel>

                <q-tab-panel name="filters" class="q-pa-none">
                    <Filters
                        :allFilters="allFilters"
                        :graphFilters="selectedGraphFilters"
                        :dashboardFilters="dashboardFilters"
                        :graphType="selectedGraph?.graphType"
                        :graphProps="selectedGraph"
                        @update:triggers="(triggers) => $emit('on-update-triggers', { triggers })"
                    />
                </q-tab-panel>

                <q-tab-panel name="graph" class="q-pa-none">
                    <ChartConfiguration
                        :graphType="selectedGraph?.graphType"
                        :graphProps="selectedGraph"
                        :attributes="attributes"
                        :categoricalAttributes="categoricalAttributes"
                        :numericAttributes="numericAttributes"
                        @graphUpdated="(payload) => $emit('on-update-graph', payload)"
                    />
                </q-tab-panel>
            </q-tab-panels>
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { ModalNameDesc } from '@/components'
import Filters from '../Dashboards/components/Filters.vue'
import ChartConfiguration from '../Dashboards/components/ChartConfiguration.vue'
import { GRAFICAS } from '../Dashboards/utils'

export default {
    name: 'ProcessDashboardsDrawerPanel',
    components: {
        ChartConfiguration,
        Filters,
        ModalNameDesc,
    },
    props: {
        editMode: VueTypes.bool.def(false),
        sideTab: VueTypes.string.def('graphsList'),
        graphObj: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),

        layoutData: VueTypes.array.def(() => []),
        dashboardFilters: VueTypes.any,
        allFilters: VueTypes.any,

        attributes: VueTypes.array.def(() => []),
        categoricalAttributes: VueTypes.array.def(() => []),
        numericAttributes: VueTypes.array.def(() => []),
    },
    emits: [
        'on-enter-edit',
        'on-save-edit',
        'on-cancel-edit',
        'on-set-side-tab',
        'on-add-graph',
        'on-update-triggers',
        'on-update-graph',
        'on-rename-graph',
    ],
    data() {
        return {
            openEditName: false,
            localTab: this.sideTab,
            graficas: GRAFICAS,
        }
    },
    computed: {
        selectedGraph() {
            return this.layoutData?.find((item) => `${item?.i}` === `${this.graphObj}`)
        },
        selectedGraphFilters() {
            return this.selectedGraph?.ownFilters || []
        },
        graphName() {
            return this.selectedGraph?.name || ''
        },
    },
    watch: {
        sideTab: {
            handler(next) {
                if (next && next !== this.localTab) this.localTab = next
            },
            immediate: true,
        },
    },
    methods: {
        handleRename(newName) {
            if (!newName?.name) return
            this.openEditName = false
            this.$emit('on-rename-graph', { name: newName.name })
        },
    },
}
</script>

<style scoped lang="scss">
.DashboardsDrawerPanel {
    width: 100%;
}
</style>
